import { ExternalLink } from 'lucide-react-native';
import { memo, useCallback } from 'react';
import { useChatContext } from 'react-native-gifted-chat';
import { twJoin } from 'tailwind-merge';

import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import type { ReferenceData } from '@/types/messageData';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { openReferenceUrl } from '@/util/chat/referenceUtils';

/**
 * Segment `reference_source` for the `REFERENCE_VIEWED` event.
 *
 * - `'suggested'`: support bubble / expert-shared reference message
 * - `'statement_attached'`: per-statement reference rendered as an
 *   inline `[N]` chip or bottom-of-bubble footnote on the
 *   ExtractedStatementsBubble.
 * - `'message_link'`: a link the recipient typed into a message's text,
 *   rather than one attached through the reference tools.
 */
export type ReferenceViewSource = 'suggested' | 'statement_attached' | 'message_link';

interface SharedReferenceViewProps {
  reference: ReferenceData | undefined;
  position: 'left' | 'right';
  onLinkClick?: (referenceId: string, url: string) => void;
  label?: string;
  /** For Segment: origin of the reference (auto-cited, support bubble, or inline message). */
  referenceSource?: ReferenceViewSource;
  /**
   * When a caption sits above the card, the card hosts inside the caption
   * column rather than the full message row, so its `max-w-[85%]` (which is
   * relative to the row) would misalign the card and its caption. Under a
   * caption the cap becomes column-relative instead. This is the same width
   * contract the other bubbles honour via their own `shrinkWidthToContent`.
   */
  shrinkWidthToContent?: boolean;
  /** Called with gifted-chat's context, as its own bubble's long press is. */
  onLongPress?: (chatContext: unknown) => void;
}

/**
 * Validates that a reference has the minimum required fields to render.
 * A URL is required; a title is not — we fall back to the URL hostname
 * when a title is missing so placeholder (non-PubMed) references aren't
 * rendered as empty bubbles.
 */
function isValidReference(ref: ReferenceData | undefined): ref is ReferenceData {
  if (!ref) {
    return false;
  }

  return typeof ref.url === 'string' && ref.url.length > 0;
}

/** Display label for the reference — title when present, hostname otherwise. */
function getDisplayTitle(reference: ReferenceData): string {
  const title = reference.title?.trim();

  if (title) {
    return title;
  }

  try {
    return new URL(reference.url).host.replace(/^www\./, '');
  } catch {
    return 'Reference';
  }
}

/**
 * Displays a shared reference within a chat message bubble.
 *
 * Renders the reference as a styled card matching the Gifted Chat bubble design.
 * Shows title, publication metadata, and abstract snippet with link to full article.
 * Adapts styling for left or right bubble position.
 *
 * Used when the expert oncologist shares a reference from the suggestion list;
 * shows the reference inline within their message.
 */
export const SharedReferenceView = memo(function SharedReferenceView({
  reference,
  position,
  onLinkClick,
  label,
  referenceSource,
  shrinkWidthToContent = false,
  onLongPress,
}: SharedReferenceViewProps) {
  const { track } = useChatAnalytics();

  const chatContext = useChatContext();

  const handleLongPress = useCallback(() => {
    onLongPress?.(chatContext);
  }, [chatContext, onLongPress]);

  const handlePress = useCallback(async () => {
    if (!isValidReference(reference)) {
      return;
    }

    track(ANALYTICS_EVENTS.REFERENCE_VIEWED, {
      reference_id: reference.external_reference_id,
      reference_title: reference.title,
      reference_url: reference.url,
      reference_journal: reference.journal,
      reference_source: referenceSource,
    });

    onLinkClick?.(reference.external_reference_id, reference.url);

    await openReferenceUrl(reference.url);
  }, [reference, onLinkClick, referenceSource, track]);

  if (!isValidReference(reference)) {
    return null;
  }

  const isLeft = position === 'left';

  const displayTitle = getDisplayTitle(reference);

  const metaParts = [
    reference.journal?.trim(),
    reference.authors?.trim(),
    reference.year && reference.year > 0 ? String(reference.year) : '',
  ].filter((part): part is string => !!part && part.length > 0);

  return (
    <Pressable
      onPress={handlePress}
      onLongPress={onLongPress ? handleLongPress : undefined}
      {...getAccessibilityProps({
        label: `Reference: ${displayTitle}${reference.authors ? ` by ${reference.authors}` : ''}`,
        role: 'link',
        hint: 'Opens the full article in a new window',
        accessible: true,
      })}
      className={twJoin('min-w-[220px]', shrinkWidthToContent ? 'max-w-full' : 'max-w-[85%]')}>
      <Box
        className={twJoin('rounded-2xl px-3 py-3', isLeft ? 'bg-background-50' : 'bg-primary-500')}>
        <VStack space={label ? 'xs' : 'sm'} className="min-w-0">
          {label ? (
            <Text
              size="xs"
              className={twJoin(
                'font-medium',
                isLeft && 'text-primary-600 opacity-80',
                !isLeft && 'text-typography-0 opacity-90'
              )}>
              {label}
            </Text>
          ) : null}
          <HStack space="xs" className="min-w-0 items-start">
            <Text
              numberOfLines={3}
              className={twJoin(
                'min-w-0 flex-1 flex-shrink font-medium text-sm underline',
                isLeft ? 'text-primary-600' : 'text-typography-0'
              )}>
              {displayTitle}
            </Text>
            <Icon
              as={ExternalLink}
              size="xs"
              className={twJoin(
                'mt-0.5 flex-shrink-0',
                isLeft ? 'text-primary-600' : 'text-typography-0'
              )}
            />
          </HStack>
          {metaParts.length > 0 ? (
            <Text
              size="xs"
              className={twJoin(
                isLeft && 'text-typography-500',
                !isLeft && 'text-typography-0 opacity-90'
              )}>
              {metaParts.join(' • ')}
            </Text>
          ) : null}
          {reference.abstract_snippet && (
            <Text
              size="xs"
              numberOfLines={2}
              className={twJoin(
                isLeft && 'text-typography-700',
                !isLeft && 'text-typography-0 opacity-80'
              )}>
              {reference.abstract_snippet}
            </Text>
          )}
        </VStack>
      </Box>
    </Pressable>
  );
});
